import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { toast } from "sonner";
import i18n from "@/i18n";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Interview,
  getCompletedInterviews,
  downloadInterviewData,
  reloadAssessment,
} from "../../../services/interview.service";
import { getAverageRating } from "../../../services/feedback.service";
import { CheckCircle2, MessageSquareText } from "lucide-react";
import { CandidateFeedbackModal } from "../../feedback/CandidateFeedbackModal";
import type { AverageRating } from "../../../types/feedback.types";
import {
  AccentBarHeading,
  IllustratedEmptyState,
  SummaryStatCard,
  SummaryStatCardGrid,
} from "@/components/ui/molecules";
import { DataTableOrganism } from "@/components/ui/organisms";
import { SummarizedListSectionTemplate } from "@/components/ui/templates";
import {
  createCompletedInterviewTableColumns,
  mapInterviewToCompletedRow,
} from "./completedInterviewsTable.columns";

interface CompletedInterviewsListProps {
  isAdmin: boolean;
  isLoading: boolean;
}

export const CompletedInterviewsList: React.FC<CompletedInterviewsListProps> = ({
  isAdmin,
  isLoading,
}) => {
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [isDownloading, setIsDownloading] = useState<{ [key: string]: boolean }>({});
  const [isReloading, setIsReloading] = useState<{ [key: string]: boolean }>({});
  const { getAccessTokenSilently } = useAuth0();
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<{
    rating: number | null;
    comment: string | null;
    candidateName: string | null;
  } | null>(null);
  const [averageRating, setAverageRating] = useState<AverageRating | null>(null);
  const [interviewsListReady, setInterviewsListReady] = useState(false);
  const hasFetchedRef = useRef(false);

  const fetchAverageRating = useCallback(async () => {
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await getAverageRating(accessToken);
      if (response.error) {
        throw response.error;
      }
      setAverageRating(response.data || null);
    } catch (error) {
      console.error("Error fetching average rating:", error);
    }
  }, [getAccessTokenSilently]);

  const fetchInterviews = useCallback(async () => {
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await getCompletedInterviews(accessToken);
      if (response.error) {
        throw response.error;
      }
      setInterviews(response.data || []);
      await fetchAverageRating();
    } catch (error) {
      console.error("Error fetching interviews:", error);
      toast.error(i18n.t('common:toasts.admin.completed_interviews_load_failed'));
    } finally {
      setInterviewsListReady(true);
    }
  }, [getAccessTokenSilently, fetchAverageRating]);

  useEffect(() => {
    if (isAdmin && !isLoading && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchInterviews();
    }
    return () => {
      if (!isAdmin || isLoading) {
        hasFetchedRef.current = false;
      }
    };
  }, [isAdmin, isLoading, fetchInterviews]);

  const handleDownload = useCallback(
    async (interviewId: string) => {
      setIsDownloading((prev) => ({ ...prev, [interviewId]: true }));
      try {
        const accessToken = await getAccessTokenSilently();
        const blob = await downloadInterviewData(accessToken, interviewId);

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `interview_${interviewId}_data.zip`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        toast.success(i18n.t('common:toasts.admin.interview_data_downloaded'));
      } catch (error) {
        console.error("Error downloading interview data:", error);
        toast.error(i18n.t('common:toasts.admin.interview_data_download_failed'));
      } finally {
        setIsDownloading((prev) => ({ ...prev, [interviewId]: false }));
      }
    },
    [getAccessTokenSilently]
  );

  const handleReloadAssessment = useCallback(
    async (interviewId: string) => {
      setIsReloading((prev) => ({ ...prev, [interviewId]: true }));
      try {
        const accessToken = await getAccessTokenSilently();
        const response = await reloadAssessment(accessToken, interviewId);

        if (response.error) {
          throw response.error;
        }

        toast.success(i18n.t('common:toasts.admin.assessment_reload_initiated'));
      } catch (error) {
        console.error("Error reloading assessment:", error);
        toast.error(i18n.t('common:toasts.admin.assessment_reload_failed'));
      } finally {
        setIsReloading((prev) => ({ ...prev, [interviewId]: false }));
      }
    },
    [getAccessTokenSilently]
  );

  const openFeedbackModal = useCallback((interview: Interview) => {
    setSelectedFeedback({
      rating: interview.feedback_rating ?? null,
      comment: interview.feedback_comment ?? null,
      candidateName: interview.candidate_name ?? null,
    });
    setIsFeedbackModalOpen(true);
  }, []);

  const closeFeedbackModal = () => {
    setIsFeedbackModalOpen(false);
    setSelectedFeedback(null);
  };

  const tableRows = useMemo(
    () => interviews.map(mapInterviewToCompletedRow),
    [interviews]
  );

  const interviewColumns = useMemo(
    () =>
      createCompletedInterviewTableColumns({
        onOpenFeedback: openFeedbackModal,
        onDownload: handleDownload,
        onReloadAssessment: handleReloadAssessment,
        isDownloading,
        isReloading,
      }),
    [
      openFeedbackModal,
      handleDownload,
      handleReloadAssessment,
      isDownloading,
      isReloading,
    ]
  );

  const emptyState = useMemo(() => {
    if (tableRows.length === 0) {
      return (
        <IllustratedEmptyState
          icon={<CheckCircle2 />}
          title="No completed interviews found."
          description="Completed interviews will appear here once available."
        />
      );
    }
    return (
      <IllustratedEmptyState
        icon={<CheckCircle2 />}
        title="No interviews found matching your search."
        description="Try adjusting your search by company, email, job, or candidate."
      />
    );
  }, [tableRows.length]);

  if (!isAdmin || isLoading) {
    return null;
  }

  if (!interviewsListReady) {
    return (
      <SummarizedListSectionTemplate
        status="loading"
        loadingMessage="Loading completed interviews…"
      />
    );
  }

  const summaryMetrics = (
    <SummaryStatCardGrid>
      <SummaryStatCard
        label="Completed Interviews"
        value={interviews.length}
        icon={<CheckCircle2 />}
      />
      <SummaryStatCard
        label="Average Rating"
        value={
          <span className="inline-flex flex-wrap items-baseline gap-x-1">
            <span>
              {averageRating?.average_rating !== null &&
              averageRating?.average_rating !== undefined
                ? averageRating.average_rating.toFixed(1)
                : "N/A"}
            </span>
            <span className="text-lg font-heading font-semibold text-dark-invirtus">/5</span>
            <span className="text-xs text-dark-invirtus/60">
              ({averageRating?.total_feedbacks || 0} feedbacks)
            </span>
          </span>
        }
        icon={<MessageSquareText />}
      />
    </SummaryStatCardGrid>
  );

  return (
    <>
      <SummarizedListSectionTemplate
        status="ready"
        sectionGap="relaxed"
        summary={summaryMetrics}
        primary={
          <DataTableOrganism
            columns={interviewColumns}
            data={tableRows}
            pageSize={10}
            enableSelection
            enableSearch
            enableColumnVisibility
            columnVisibilityLabel="Column visibility"
            toolbarTitle={<AccentBarHeading>Interview Archive</AccentBarHeading>}
            searchPlaceholder="Search by company, email, job, candidate…"
            searchKeys={[
              "companyName",
              "creatorEmail",
              "jobTitle",
              "candidateName",
              "interviewId",
            ]}
            paginationItemLabel="interviews"
            dataTableProps={{
              emptyState,
            }}
          />
        }
      />
      {selectedFeedback && (
        <CandidateFeedbackModal
          isOpen={isFeedbackModalOpen}
          onClose={closeFeedbackModal}
          variant="admin"
          rating={selectedFeedback.rating}
          comment={selectedFeedback.comment || ""}
          candidate={selectedFeedback.candidateName || "Candidate"}
          maxCommentLength={500}
          onRatingChange={() => {}}
          onCommentChange={() => {}}
          onSubmit={() => {}}
          isSubmitting={false}
        />
      )}
    </>
  );
};
